import { Banner, Button, Screen, ScreenLead, ScreenTitle, Spinner } from '@akpl/ui-mobile';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { API_BASE } from './api';
import { ConfigProvider } from './appConfig';
import { AuthProvider } from './auth';
import { type BootResult, runBoot } from './boot';
import { createAppRouter } from './router';

type BootState =
  | { status: 'booting' }
  | { status: 'ready'; boot: BootResult }
  | { status: 'error'; message: string };

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 10_000 },
  },
});

/**
 * Boots (`/config` → i18n → Mixpanel → Firebase) and then mounts the router (docs/05 §1).
 * Nothing renders before `/config` resolves because both the API client and Firebase need it;
 * the splash is the same flat dark screen as every route, so there is no flash of white.
 */
export function App() {
  const [state, setState] = useState<BootState>({ status: 'booting' });
  const [attempt, setAttempt] = useState(0);
  const router = useMemo(() => createAppRouter(), []);

  useEffect(() => {
    let cancelled = false;
    runBoot()
      .then((boot) => {
        if (!cancelled) setState({ status: 'ready', boot });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ status: 'error', message: err instanceof Error ? err.message : String(err) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  if (state.status === 'booting') {
    return (
      <Screen>
        <Spinner label="Acapella" />
      </Screen>
    );
  }

  if (state.status === 'error') {
    return (
      <Screen data-testid="boot-error">
        <ScreenTitle>Acapella</ScreenTitle>
        <Banner tone="error">{state.message}</Banner>
        <ScreenLead>{API_BASE}</ScreenLead>
        <Button onClick={() => setAttempt((n) => n + 1)}>Retry</Button>
      </Screen>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider value={state.boot.config}>
        <AuthProvider client={state.boot.firebase}>
          <RouterProvider router={router} />
        </AuthProvider>
      </ConfigProvider>
    </QueryClientProvider>
  );
}
